<template>
  <nav
    v-if="breadcrumbs.length"
    class="breadcrumbs"
    >
    <!-- Frontpage dropdown -->
    <div class="breadcrumb">
      <v-menu
        location="bottom"
        offset="4"
        >
        <template #activator="{ props }">
          <RouterLink
            to="/"
            v-bind="props"
            class="breadcrumb-link"
            >
            {{ t('Frontpage_link') }}
          </RouterLink>
        </template>
        <v-list>
          <v-list-item
            v-for="route in topLevelRoutes"
            :key="route.path"
            :to="route.path"
            :title="t(String(route.meta?.titleKey || route.name || '—'))"
            link
          />
        </v-list>
      </v-menu>
      <span class="dot">•</span>
    </div>

    <!-- Breadcrumb segments -->
    <template
        v-for="(crumb, index) in breadcrumbs"
        :key="crumb.path"
        >
      <div class="breadcrumb">
        <!-- Dropdown if route has children (not for last item) -->
        <v-menu
          v-if="crumb.children?.length && index !== breadcrumbs.length - 1"
          open-on-hover
          location="bottom"
          offset="4"
        >
          <template #activator="{ props }">
            <span
              v-bind="props"
              class="breadcrumb-link"
              :class="{ 'is-last': index === breadcrumbs.length - 1 }"
              style="cursor: pointer;"
            >
              {{ crumb.name }}
              <ChevronIcon />
            </span>
          </template>
          <v-list>
            <v-list-item
              v-for="child in crumb.children"
              :key="child.path"
              :to="child.path"
              :title="child.name"
              link
            />
          </v-list>
        </v-menu>

        <!-- Non-dropdown link (not navigatable) -->
        <span
          v-else
          class="breadcrumb-link"
          :class="{ 'is-last': index === breadcrumbs.length - 1 }"
        >
          {{ crumb.name }}
        </span>

        <span
          v-if="index !== breadcrumbs.length - 1"
          class="dot"
        >•</span>
      </div>
    </template>
  </nav>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ChevronIcon from '@/components/shared/ChevronIcon.vue'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

// Top-level routes (excluding wildcard or param paths)
const topLevelRoutes = computed(() =>
  router.getRoutes().filter(
    r =>
      r.path !== '/:pathMatch(.*)*' &&
      !r.path.includes('/:') &&
      r.path !== '/'
  )
)

const breadcrumbs = computed(() =>
  route.matched
    .filter(r => r.meta?.breadcrumb !== false)
    .map((r) => {
      const name = t(String(r.meta?.breadcrumb || r.meta?.titleKey || r.name || '—'))
      const path = router.resolve({ path: r.path }).href

      // Get children for dropdown - use the route definition, not the matched path
      let children = r.children ?? []
      
      // For Umsóknir, get direct children (categories) from the router definition
      if (r.path === '/umsoknir') {
        const umsoknirRoute = router.getRoutes().find(route => route.path === '/umsoknir')
        children = umsoknirRoute?.children ?? []
      }

      const childLinks = children.map(child => {
        let childPath: string
        
        // If parent is /umsoknir, construct path as /umsoknir/{child}
        if (r.path === '/umsoknir') {
          childPath = `/umsoknir/${child.path}`
        } else {
          // Otherwise, use router.resolve for relative paths
          childPath = router.resolve(child.path).href
        }
        
        return {
          name: t(String(child.meta?.titleKey || child.meta?.breadcrumb || child.name || '—')),
          path: childPath
        }
      })

      return {
        name,
        path,
        children: childLinks.length > 0 ? childLinks : undefined
      }
    })
)
</script>

<style scoped lang="scss">
.breadcrumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  margin: 16px 0 32px;
  align-items: center;
}

.breadcrumb {
  display: flex;
  align-items: center;
}

.breadcrumb-link {
  color: var(--v-theme-primary-dark);
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;

  &.is-last {
    font-weight: 600;
    pointer-events: none;
    color: var(--v-theme-on-background);
  }
}

.dot {
  color: #aaa;
  margin: 0 6px;
}

.v-list-item {
  min-width: 160px;
}
</style>
